/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.loadedProductData = new Set();
      this.productDataRequests = new Map();
      this.productDataAbortController = null;
      this.pendingProductDataRequestToken = 0;
      this.activeProductDataUrl = this.normaliseProductUrl(this.dataset.url);
      if (this.activeProductDataUrl) {
        this.loadedProductData.add(this.activeProductDataUrl);
      }
      this.ensureValidVariantSelected();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.addEventListener('click', this.handleSwatchLoadingStart.bind(this));
      this.applySearchParams();
      this.updataMainBackorderText();
      this.setAttribute('loaded', '');
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    async handleVariantChange(evt) {
      const url = evt.target.getAttribute('data-url')
      if(url){
        this.setAttribute('data-url',url)
      }
      const requestToken = ++this.pendingProductDataRequestToken;
      const shouldShowSwatchLoading = !!this.getLoadingSwatchLabelFromEvent(evt);
      const swatchLoadingOwner = shouldShowSwatchLoading ? `variant-picker:${requestToken}` : null;
      const swatchLoadingStartedAt = shouldShowSwatchLoading ? performance.now() : 0;
      const targetProductUrl = this.normaliseProductUrl(this.dataset.url);

      if (shouldShowSwatchLoading) {
        this.beginSwatchLoading(evt, swatchLoadingOwner, true);
      }

      try {
        if (targetProductUrl && targetProductUrl !== this.activeProductDataUrl) {
          await this.ensureProductDataLoaded(targetProductUrl);
        }

        if (requestToken !== this.pendingProductDataRequestToken) return;

        this.ensureValidVariantSelected();
        this.selectedOptions = this.getSelectedOptions();
        this.variant = this.getSelectedVariant();
        this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;
        this.updateUrl(evt);
        this.updateVariantInput();
        this.updateAddToCartButton();
        this.updateAvailability();
        this.updatePrice();
        this.updateBackorderText();
        this.updataMainBackorderText();
        this.updatePickupAvailability();
        this.updateSku();
        this.updateBarcode();
        VariantPicker.updateLabelText(evt);
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            allVariants: this.data.variants,
            selectedOptions: this.selectedOptions,
            trigger: evt.target
          }
        }));
      } catch (error) {
        if (error.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('Failed to load variant picker data', error);
        }
        return;
      } finally {
        if (shouldShowSwatchLoading && requestToken === this.pendingProductDataRequestToken) {
          const remainingDuration = 450 - (performance.now() - swatchLoadingStartedAt);
          if (remainingDuration > 0) {
            await new Promise((resolve) => {
              setTimeout(resolve, remainingDuration);
            });
          }
        }

        if (shouldShowSwatchLoading) {
          this.endSwatchLoading(swatchLoadingOwner);
        }
      }
    }

    handleSwatchLoadingStart(evt) {
      const label = evt.target.closest('.opt-label');
      if (!label) return;

      const optionSelector = label.closest('.option-selector');
      const optionName = optionSelector?.dataset?.option?.trim()?.toLowerCase();
      if (optionName !== 'color' && optionName !== 'colour') return;

      const inputId = label.getAttribute('for');
      if (!inputId) return;

      const input = this.querySelector(`#${CSS.escape(inputId)}`);
      if (!input || input.checked) return;

      this.beginSwatchLoading({ target: input }, 'variant-picker:click-preview', true);
    }

    getLoadingSwatchLabelFromEvent(evt) {
      const trigger = evt?.target || evt?.detail?.trigger;
      if (!trigger) return null;

      const input = trigger.matches('.js-option') ? trigger : trigger.closest('.js-option');
      if (!input) return null;

      const optionSelector = input.closest('.option-selector');
      const optionName = optionSelector?.dataset?.option?.trim()?.toLowerCase();
      if (optionName !== 'color' && optionName !== 'colour') return null;

      const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`)
        || (input.nextElementSibling && input.nextElementSibling.matches('label') ? input.nextElementSibling : null);

      return label && label.classList.contains('opt-label') ? label : null;
    }

    clearLoadingSwatch() {
      if (this.loadingSwatchSpinner && this.loadingSwatchSpinner.parentNode) {
        this.loadingSwatchSpinner.remove();
      }

      if (this.loadingSwatchLabel) {
        this.loadingSwatchLabel.classList.remove('is-media-loading');
        this.loadingSwatchLabel.removeAttribute('aria-busy');
      }

      this.loadingSwatchLabel = null;
      this.loadingSwatchSpinner = null;
      this.swatchLoadingOwners = new Set();
    }

    beginSwatchLoading(evt, ownerKey, reset = false) {
      const label = this.getLoadingSwatchLabelFromEvent(evt);

      if (reset) {
        this.clearLoadingSwatch();
      } else if (!this.swatchLoadingOwners) {
        this.swatchLoadingOwners = new Set();
      }

      if (!label) return false;

      if (this.loadingSwatchLabel !== label) {
        this.clearLoadingSwatch();
      }

      if (!this.swatchLoadingOwners) {
        this.swatchLoadingOwners = new Set();
      }

      label.classList.add('is-media-loading');
      label.setAttribute('aria-busy', 'true');

      if (!this.loadingSwatchSpinner || this.loadingSwatchSpinner.parentNode !== label) {
        const spinner = document.createElement('span');
        spinner.className = 'opt-label__loading-spinner';
        spinner.setAttribute('aria-hidden', 'true');
        label.appendChild(spinner);
        this.loadingSwatchSpinner = spinner;
      }

      this.loadingSwatchLabel = label;

      if (ownerKey) {
        this.swatchLoadingOwners.add(ownerKey);
      }

      return true;
    }

    endSwatchLoading(ownerKey) {
      if (!ownerKey) {
        this.clearLoadingSwatch();
        return;
      }

      if (!this.swatchLoadingOwners) {
        this.swatchLoadingOwners = new Set();
      }

      this.swatchLoadingOwners.delete(ownerKey);

      if (!this.swatchLoadingOwners.size) {
        this.clearLoadingSwatch();
      }
    }

    normaliseProductUrl(url) {
      if (!url) return '';

      try {
        return new URL(url, window.location.origin).pathname;
      } catch (error) {
        return url;
      }
    }

    async ensureProductDataLoaded(productUrl) {
      if (!productUrl || this.loadedProductData.has(productUrl)) {
        this.activeProductDataUrl = productUrl || this.activeProductDataUrl;
        return;
      }

      if (!this.productDataRequests.has(productUrl)) {
        if (this.productDataAbortController) {
          this.productDataAbortController.abort('Superseded by newer product data request');
        }

        this.productDataAbortController = new AbortController();
        this.productDataRequests.set(productUrl, this.fetchProductData(productUrl, this.productDataAbortController.signal)
          .then((productData) => {
            if (!productData) return;

            this.mergeProductData(productData);
            this.loadedProductData.add(productUrl);
            this.activeProductDataUrl = productUrl;
          })
          .finally(() => {
            this.productDataRequests.delete(productUrl);
            if (this.productDataAbortController && !this.productDataRequests.size) {
              this.productDataAbortController = null;
            }
          }));
      } else {
        this.activeProductDataUrl = productUrl;
      }

      await this.productDataRequests.get(productUrl);
    }

    async fetchProductData(productUrl, signal) {
      const requestUrl = new URL(productUrl, window.location.origin);
      requestUrl.searchParams.set('view', 'variant-picker-data');

      const response = await fetch(requestUrl.toString(), {
        headers: {
          Accept: 'application/json'
        },
        signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.json();
    }

    mergeProductData(productData) {
      if (!productData || !Array.isArray(productData.variants)) return;

      const variantsById = new Map(this.data.variants.map((variant) => [variant.id, variant]));
      productData.variants.forEach((variant) => {
        variantsById.set(variant.id, variant);
      });
      this.data.variants = Array.from(variantsById.values());

      this.data.formatted = {
        ...this.data.formatted,
        ...(productData.formatted || {})
      };
    }


    ensureValidVariantSelected(){

      let currentOptions = this.getSelectedOptions();
      let variant = this.data.variants.find(v => v.options.every((val, idx) => val === currentOptions[idx]));
      if (!variant) {
        const availableVariants = this.data.variants.filter(v => {
          return v.options[0] === currentOptions[0]; 
        });

        if (availableVariants.length > 0) {
          const fallbackOptions = availableVariants[0].options;
          this.optionSelectors.forEach((selector, index) => {
            const targetValue = fallbackOptions[index];
            if (currentOptions[index] === targetValue) return;

            if (selector.dataset.selectorType === 'dropdown') {
              const targetOption = selector.querySelector(`.custom-select__option[data-value="${targetValue}"]`);
              if (targetOption) {
                const customSelect = targetOption.closest('custom-select');
                if (customSelect && customSelect.selectOption) {
                  customSelect.selectOption(targetOption);
                }
              }
            } else {
              const targetInput = selector.querySelector(`input[value="${targetValue}"]`);
              if (targetInput) {
                targetInput.checked = true;
              }
            }
          });
        }
      }
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      let variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
      if(this.variant){
        const { inventory,preLimit } = this.data.formatted[this.variant.id];
        if(preLimit===0){
          variantAvailable = false
        }
        this.addBtn.disabled = !variantAvailable;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }else{
          this.addBtn.disabled = true
          this.addBtn.textContent = unavailableStr

      }

    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      if(!el.classList.contains('freeze')){
        el.classList.toggle('is-unavailable', !available);
      }
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'off') return;
      const { availabilityMode } = this.dataset; // 'down' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }
      

      if (availabilityMode === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  const { inventory,preLimit } = this.data.formatted[v.id];
                  if(inventory == 'none' && preLimit == 0){
                    variantsAvailable = false
                  }else{
                    variantsAvailable = true;
                  }
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;
      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];
        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

        //  backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          backorderProdEl.textContent = `Pre-Order:`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    updataMainBackorderText(){
      this.mainBackorder = this.mainBackorder || this.section.querySelector('.preorder-terms-wrapper');
      const propertyInput = this.mainBackorder?.querySelector('.preorder-input-hidden-property');
      const propertySaleMsgInput = this.mainBackorder?.querySelector('.sale-input-hidden-property');
      const checkbox = this.mainBackorder?.querySelector('.preorder-input-hidden');
      const preorderShipping = this.mainBackorder?.querySelector('.preorder-shipping-message');
      if (!this.mainBackorder) return;
      let preOrderShippingMsg = preorderShipping?.getAttribute('data-default') || '';

      let hideBackorder = true;
      const { inventory,preLimit,hidePreOrderiInfo,preOrderShippingMessage,compareAtPrice,price } = this.data.formatted[this.variant.id];

      if(compareAtPrice){
        const numA = parseFloat(compareAtPrice.replace('$', ''));
        const numB = parseFloat(price.replace('$', ''));

        if (numA > numB) {
          if (propertySaleMsgInput) {
            propertySaleMsgInput.disabled = false;
          }
        } else if (propertySaleMsgInput) {
          propertySaleMsgInput.disabled = true;
        }
      }


      if(preOrderShippingMessage){
        preOrderShippingMsg = preOrderShippingMessage
      }
      if (this.variant && this.variant.available) {
        if (this.variant.inventory_management && inventory === 'none') {
          if(preLimit===0){
            hideBackorder = true;
          }else{
            hideBackorder = false;
          }
        }
      }
     

      if (propertyInput) {
        propertyInput.disabled = hideBackorder;
      }
      if (checkbox) {
        checkbox.disabled = hideBackorder;
      }
      this.mainBackorder.hidden = hideBackorder;
      this.mainBackorder.classList.remove('hidden');
      if(hideBackorder){
          this.mainBackorder.classList.add('hidden');
      }
      if (propertyInput) {
        propertyInput.name = "properties[Pre-Order]";
      }
      if(hidePreOrderiInfo==true){
        this.mainBackorder.classList.add('hidden');
      //  propertyInput.disabled = true
        if (propertyInput) {
          propertyInput.name = "properties[_Pre-Order]";
        }
        if (checkbox) {
          checkbox.disabled =  true;
        }
      }

      if(preorderShipping){
        if (propertyInput) {
          propertyInput.value = preOrderShippingMsg;
        }
        preorderShipping.innerHTML = preOrderShippingMsg;
      }
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
        static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;
      const bindId =  evt.target.closest('.option-selector__btns').dataset.bindId;
      const colorTexts = selector.querySelectorAll('.js-color-text');
      if (!colorTexts) return;
      colorTexts.forEach((el)=>{
        if(el.dataset.bind == bindId){
           el.textContent = ': ' + evt.target.nextElementSibling.querySelector('.js-value').textContent;
        }else{
          el.textContent = ''
        }

      })
      // if(bindId){
       
        
      //   colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
      // }else{
      //   const colorText = selector.querySelector('.js-color-text');
      //   if (!colorText) return;
      //   colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
      // }

    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability &&  this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = VariantPicker.trimTrailingZeros(this.data.formatted[variant.id].price);
        if (priceWasEl) priceWasEl.innerHTML = VariantPicker.trimTrailingZeros(this.data.formatted[variant.id].compareAtPrice || '');

        // Update unit price, if specified.
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = VariantPicker.trimTrailingZeros(this.data.formatted[variant.id].unitPrice);
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    static trimTrailingZeros(value) {
      if (!value || typeof value !== 'string') return value;
      return value.replace(/([,.]\d*?[1-9])0+(?=(?:\s|$|<))/g, '$1').replace(/([,.])0+(?=(?:\s|$|<))/g, '');
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      let url = new URL(window.location.href);
      if(this.variant){
        const params = url.searchParams;
        params.set('variant', this.variant.id);
      }else{
        url = this.dataset.url
      }
      window.history.replaceState({ }, '', url);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');
      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];
    
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.data.variants.find(
        (v) => v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }

    /**
     * Apply search parameters.
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${value}"]`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${value}"]`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
